import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { apiRequest } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user

  // 🔄 Validar sesión al cargar la app
  useEffect(() => {
    apiRequest('/api/auth/me')
      .then((res) => setUser(res.user))
      .catch(() => {
        setUser(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  // Completa la acción que llevó al usuario a identificarse (incluye OAuth).
  useEffect(() => {
    if (!user) return
    let propertyId = ''
    try { propertyId = sessionStorage.getItem('nido:pending-favorite') || '' } catch { return }
    if (!propertyId) return

    const alreadySaved = (user.favorites || [])
      .some((favorite) => String(favorite?._id || favorite) === propertyId)
    if (alreadySaved) {
      try { sessionStorage.removeItem('nido:pending-favorite') } catch {}
      return
    }

    // Se retira antes de solicitar para impedir un doble toggle bajo StrictMode.
    try { sessionStorage.removeItem('nido:pending-favorite') } catch {}
    apiRequest(`/api/users/favorites/${encodeURIComponent(propertyId)}`, { method: 'POST' })
      .then((data) => setUser((current) => current ? { ...current, favorites: data.favorites || [] } : current))
      .catch(() => {
        try { sessionStorage.setItem('nido:pending-favorite', propertyId) } catch {}
      })
  }, [user])

  // 🔐 Login normal
  const login = useCallback(async ({ email, password }) => {
    await apiRequest('/api/auth/login', {
      method: 'POST',
      auth: false,
      body: { email: email.trim().toLowerCase(), password },
    })

    // No damos la sesión por iniciada sólo porque el POST respondió bien:
    // comprobamos que la cookie quedó activa antes de cambiar de pantalla.
    try {
      const me = await apiRequest('/api/auth/me')
      if (!me?.user) throw new Error('SESSION_NOT_PERSISTED')
      setUser(me.user)
      return me.user
    } catch (error) {
      setUser(null)
      const sessionError = new Error(
        'Ingresaste correctamente, pero no pudimos conservar la sesión. Revisá que el navegador permita cookies para NIDO e intentá nuevamente.'
      )
      sessionError.code = 'SESSION_NOT_PERSISTED'
      throw sessionError
    }
  }, [])

  const refreshSession = useCallback(async () => {
    const me = await apiRequest('/api/auth/me')
    setUser(me.user)
  }, [])

  // 🚪 Logout
  const logout = useCallback(async () => {
    await apiRequest('/api/auth/logout', { method: 'POST' }).catch(() => {})
    localStorage.removeItem('verifyEmail')
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,        // 🔥 ahora disponible
        isAuthenticated,
        loading,
        login,
        refreshSession,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
